import type { OperatorRole } from "@/shared/api/types";

/**
 * Operator roles (ADR-0011, runbooks/platform.md): `operator` reads everything, opens support sessions and
 * retries jobs; `admin` also changes tenant lifecycle and plans, and manages operators and reviewer codes.
 * The catalogue below is the ONE list of admin-only actions; the nav inventory test and the UI gates read it,
 * and the API enforces the same gate (`requireOperatorAdmin`).
 */
export const ADMIN_ACTIONS = [
  "tenant.activate",
  "tenant.suspend",
  "tenant.unsuspend",
  "tenant.delete_request",
  "tenant.cancel_delete",
  // M11 (ADR-0015 §5): the catalogue is platform-admin only, and so is every action that moves an
  // organisation's money. `billing.cancel_subscription` went with the Razorpay mandate it cancelled.
  "catalog.manage",
  "billing.manage",
  "queue.retry",
  "operators.manage",
  "reviewers.manage",
  // M14 follow-up (ADR-0007 addendum 2026-09-15): the device_models capacity editor is a product-wide
  // fact, like the catalogue — any operator reads it, only an admin changes a number or verifies one.
  "device_models.manage",
  // M20 (ADR-0024): the platform's SMS price, minimum, trial credits, SAC and templates registry are
  // product-wide facts like the catalogue, and an adjustment moves one organisation's money — any
  // operator reads all of it, only an admin writes (the API: `requireOperatorAdmin`).
  "sms.manage",
  // M24 (ADR-0026, I1): lifting one seat's holder or phone change limit for the day is a platform decision
  // about one organisation's seat — any operator reads the Face Kiosk card, only an admin overrides
  // (the API: `requireOperatorAdmin` on the override route, S3 route 13).
  "face_kiosk.override",
  // M26 (ADR-0031 d7–d8, contracts §22): statutory rule sets and tax years are law that every organisation
  // with Payroll computes from. Any operator reads them (and previews); only an admin drafts, edits, publishes
  // or withdraws (the API: `requireOperatorAdmin` on routes 41–44 and 50–53).
  "payroll_law.manage",
  // All operators may inspect mobile releases; only admins may retire a build or publish an update.
  "mobile_releases.manage",
] as const;
export type AdminAction = (typeof ADMIN_ACTIONS)[number];

export function isAdmin(role: OperatorRole): boolean {
  return role === "admin";
}

/** `can(role, "tenant.suspend")`: admin does everything; an operator does everything not in the catalogue. */
export function can(role: OperatorRole, action: AdminAction): boolean {
  return isAdmin(role) || !ADMIN_ACTIONS.includes(action);
}
